import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiCategory, type FeedLayout } from '@b4b/shared';
import { useClustersFeed, useFeeds, useSubscribe, useSubscribePack, useSettings } from '../api/queries.js';
import { usePackGroups } from '../lib/packs.js';
import ReelsFeed from '../components/feed/ReelsFeed.js';
import ListFeed from '../components/feed/ListFeed.js';
import GridFeed from '../components/feed/GridFeed.js';
import TopicsFeed from '../components/feed/TopicsFeed.js';
import type { FeedParams } from '../lib/feedUrl.js';
import { useQueryClient } from '@tanstack/react-query';

// Main feed: reads filters from the URL and the layout from server-side settings,
// then hands the data to one of the layout components (reels / list / grid / topics).
export default function Feed() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const settings = useSettings();
  const feeds = useFeeds();
  const qc = useQueryClient();

  const topicParam = aiCategory.safeParse(searchParams.get('topic'));
  const params: FeedParams = {
    filter: searchParams.get('filter') === 'all' ? 'all' : 'unread',
    category: searchParams.get('cat') ?? undefined,
    tag: searchParams.get('tag') ?? undefined,
    topic: topicParam.success ? topicParam.data : undefined,
  };

  // a topic drill-down (or a tag filter, which sections cannot express) is a flat list
  const layoutSetting: FeedLayout = settings.data?.feedLayout ?? 'reels';
  const layout: FeedLayout =
    layoutSetting === 'topics' && (params.topic || params.tag) ? 'list' : layoutSetting;

  // don't fire the list query until the layout is known (topics uses its own query)
  const clusters = useClustersFeed(params, { enabled: settings.isSuccess && layout !== 'topics' });
  const items = clusters.items;
  const pager = { hasMore: clusters.hasNextPage, loading: clusters.isFetchingNextPage, loadMore: () => void clusters.fetchNextPage() };
  const hasFeeds = (feeds.data?.length ?? 0) > 0;

  // freshly subscribed feeds are still being fetched server-side — poll until
  // articles arrive instead of showing a misleading "all read"
  const firstFetchPending = (feeds.data ?? []).some((feed) => feed.lastFetchedAt === null);
  const fetchingFirstArticles =
    firstFetchPending && (layout === 'topics' || (clusters.isSuccess && items.length === 0));

  useEffect(() => {
    if (!firstFetchPending) return;
    const timer = setInterval(() => {
      void feeds.refetch();
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    }, 4000);
    return () => clearInterval(timer);
  }, [firstFetchPending, feeds, qc]);

  if (feeds.isSuccess && !hasFeeds) {
    return <EmptyState />;
  }

  // isPending (not isLoading): during persisted-cache restore fetchStatus is idle, so
  // isLoading would be false with no data and reels would flash an empty "all done" slide
  if (settings.isPending || (layout !== 'topics' && clusters.isPending)) {
    return <p className="py-24 text-center text-neutral-500">{t('common.loading')}</p>;
  }
  if (clusters.isError) {
    return (
      <p className="py-24 text-center text-neutral-500">
        {t('common.error')}{' '}
        <button onClick={() => void clusters.refetch()} className="text-accent underline">
          {t('common.retry')}
        </button>
      </p>
    );
  }

  switch (layout) {
    case 'list':
      return <ListFeed items={items} params={params} pager={pager} fetchingFirstArticles={fetchingFirstArticles} />;
    case 'grid':
      return <GridFeed items={items} params={params} pager={pager} fetchingFirstArticles={fetchingFirstArticles} />;
    case 'topics':
      return <TopicsFeed params={params} fetchingFirstArticles={fetchingFirstArticles} />;
    default:
      return <ReelsFeed items={items} params={params} pager={pager} fetchingFirstArticles={fetchingFirstArticles} />;
  }
}

function EmptyState() {
  const { t } = useTranslation();
  const subscribe = useSubscribe();
  const [url, setUrl] = useState('');
  const [addedPacks, setAddedPacks] = useState<string[]>([]);
  const { lang, groups } = usePackGroups();

  const subscribePack = useSubscribePack();
  const addPack = (id: string) => {
    subscribePack.mutate(id, { onSuccess: () => setAddedPacks((prev) => [...prev, id]) });
  };

  return (
    <div className="mx-auto max-w-md space-y-6 px-4 pb-10 pt-24 text-center">
      <div>
        <h2 className="text-2xl font-bold">{t('feed.empty.title')}</h2>
        <p className="mt-2 text-neutral-500">{t('feed.empty.subtitle')}</p>
      </div>

      {groups.map((group) => (
        <section key={group.key} className="space-y-3 text-left">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{t(`feed.empty.${group.key}`)}</h3>
          <div className="grid grid-cols-2 gap-3">
            {group.packs.map((pack) => (
              <button
                key={pack.id}
                disabled={addedPacks.includes(pack.id) || subscribePack.isPending}
                onClick={() => addPack(pack.id)}
                className="rounded-2xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition hover:border-accent disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <span className="text-lg font-semibold">
                  {pack.emoji} {pack.name[lang] ?? pack.name.cs}
                </span>
                <p className="mt-1 text-xs text-neutral-500">
                  {pack.feeds.map((f) => f.title).join(' · ')}
                </p>
              </button>
            ))}
          </div>
        </section>
      ))}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) subscribe.mutate({ feedUrl: url.trim() });
        }}
        className="flex gap-2"
      >
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={t('sources.addPlaceholder')}
          className="flex-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
        />
        <button
          type="submit"
          className="rounded-xl bg-accent px-4 py-2 font-medium text-white hover:bg-accent-strong"
        >
          {t('feed.empty.addUrl')}
        </button>
      </form>
    </div>
  );
}
